import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { LANDING_VIEW, pathToView, viewToPath, type View } from '../lib/viewRouter';

type ViewState = {
  currentView: View;
  hasSeenWelcome: boolean;
  setCurrentView: (view: View) => void;
  /** Set the view WITHOUT pushing a new history entry (used when reacting to URL changes). */
  setViewFromUrl: (view: View) => void;
  setHasSeenWelcome: (seen: boolean) => void;
};

function pushUrlForView(view: View) {
  if (typeof window === 'undefined') return;
  const entityId =
    view === 'domain'
      ? localStorage.getItem('selectedDomainId')
      : view === 'profile'
        ? localStorage.getItem('selectedProfileId')
        : null;
  const path = viewToPath(view, entityId);
  if (window.location.pathname !== path) {
    window.history.pushState({ view }, '', path);
  }
}

export const useCurrentView = create<ViewState>()(
  persist<ViewState, [], [], any>(
    (set) => ({
      // ⭐ The Roadmap, not Home: Home is gated until step 4 (owner, 2026-09-29).
      currentView: LANDING_VIEW,
      hasSeenWelcome: false,
      setCurrentView: (view) => {
        set({ currentView: view });
        // Reflect navigation in the URL so back/forward + deep-links work.
        pushUrlForView(view);
      },
      setViewFromUrl: (view) => set({ currentView: view }),
      setHasSeenWelcome: (seen) => set({ hasSeenWelcome: seen }),
    }),
    {
      name: 'view-storage',
      /**
       * ⚠️⚠️ BUMPED FOR THE `social` → `discover` RENAME. This store persists the
       * last view someone was on, so every user who closed the app on Connect
       * has `'social'` written to disk. Without this migration they reopen onto
       * a view that no longer exists and fall through to a blank switch — the
       * app looks broken on launch, for exactly the people who used it most.
       */
      version: 3,
      migrate: (persisted: any, from: number) => {
        if (from < 2 && persisted?.currentView === 'social') {
          return { ...persisted, currentView: 'discover' };
        }
        /* ⚠️⚠️ v3: the Domains page was retired (2026-10-03). Somebody who closed the
           app on it has `'domains'` on disk — the same blank-switch trap as above.
           They reopen on Roadmap → Domain Progression, which holds the same content. */
        if (from < 3 && persisted?.currentView === 'domains') {
          try { localStorage.setItem('roadmapTrack', 'domains'); } catch { /* refused — Roadmap opens on its default */ }
          return { ...persisted, currentView: 'roadmap' };
        }
        return persisted;
      },
    }
  )
);

/** Where a sign-in lands: Home (owner, 2026-10-05). */
export const HOME_VIEW: View = 'dashboard';

/**
 * Point the app at Home for the next sign-in, instead of the page that was open
 * when the last session ended. Called on sign-out AND on a successful sign-in.
 *
 * ⚠️ The URL is reset too, not only the store: `useViewUrlSync` lets a URL that
 * names a view WIN over the persisted one on load, so leaving `/calendar` in the
 * address bar would reopen the Calendar on the next page load.
 *
 * ⚠️ No reveal check here on purpose. Home is gated until step 4, and App's
 * `resolveLandingView` already sends a person who can't see Home to the Roadmap.
 * Asking twice would need a second copy of that gate.
 *
 * Any other path (e.g. `/assess/:token`) is left alone.
 */
export function landOnHome() {
  useCurrentView.setState({ currentView: HOME_VIEW });
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  const isAppPath = path === '/' || path === '' || pathToView(path) !== null;
  if (isAppPath && path !== viewToPath(HOME_VIEW)) {
    window.history.replaceState({ view: HOME_VIEW }, '', viewToPath(HOME_VIEW));
  }
}
